import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Email,
  Campaign,
  DeleteOutline,
  Edit,
  ContentCopy,
  Facebook,
  Twitter,
  Instagram,
  Send,
  Add,
  ArrowUpward,
  InsertLink,
  Analytics,
  People,
  LinkedIn
} from '@mui/icons-material';

// Mock campaigns data
const campaignData = [
  {
    id: 1,
    name: 'Summer Promotion',
    type: 'email',
    status: 'active',
    audience: 'All Users',
    sentDate: '2023-05-10',
    openRate: 32,
    clickRate: 8.5,
    conversionRate: 2.1
  },
  {
    id: 2,
    name: 'New Feature Announcement',
    type: 'email',
    status: 'scheduled',
    audience: 'Pro Users',
    sentDate: '2023-06-01',
    openRate: null,
    clickRate: null,
    conversionRate: null
  },
  {
    id: 3,
    name: 'Limited Time Offer',
    type: 'social',
    status: 'ended',
    audience: 'All Users',
    sentDate: '2023-04-15',
    openRate: 28,
    clickRate: 7.2,
    conversionRate: 3.4
  },
  {
    id: 4,
    name: 'Webinar Invitation',
    type: 'email',
    status: 'draft',
    audience: 'Enterprise Users',
    sentDate: null,
    openRate: null,
    clickRate: null,
    conversionRate: null
  },
];

// Mock audience segments data
const audienceSegments = [
  { id: 1, name: 'All Users', count: 5482 },
  { id: 2, name: 'Pro Users', count: 1254 },
  { id: 3, name: 'Enterprise Users', count: 368 },
  { id: 4, name: 'Trial Users', count: 927 },
  { id: 5, name: 'Inactive Users', count: 2153 },
];

// Helper function for status colors
const statusColors = {
  active: 'success',
  scheduled: 'primary',
  draft: 'default',
  ended: 'error'
};

function Marketing() {
  const [activeTab, setActiveTab] = useState(0);
  const [successMessage] = useState('');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Marketing</Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable" 
          scrollButtons="auto"
        >
          <Tab label="Overview" icon={<Analytics />} iconPosition="start" />
          <Tab label="Campaigns" icon={<Campaign />} iconPosition="start" />
          <Tab label="Email Marketing" icon={<Email />} iconPosition="start" />
          <Tab label="Social Media" icon={<Facebook />} iconPosition="start" />
          <Tab label="Audience" icon={<People />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Performance Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Campaigns
                </Typography>
                <Typography variant="h3">
                  {campaignData.length}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Active: {campaignData.filter(c => c.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Draft: {campaignData.filter(c => c.status === 'draft').length}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Avg. Open Rate
                </Typography>
                <Typography variant="h3">
                  30.2%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={30.2} 
                  sx={{ my: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="success.main">
                  +2.4% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Avg. Click Rate
                </Typography>
                <Typography variant="h3">
                  7.8%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={7.8} 
                  color="secondary"
                  sx={{ my: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="success.main">
                  +1.2% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Conversion Rate
                </Typography>
                <Typography variant="h3">
                  2.6%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={26} 
                  color="success"
                  sx={{ my: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="success.main">
                  +0.5% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Recent Campaign Performance */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Recent Campaign Performance</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Sent Date</TableCell>
                      <TableCell>Open Rate</TableCell>
                      <TableCell>Click Rate</TableCell>
                      <TableCell>Conversion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaignData.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>{campaign.name}</TableCell>
                        <TableCell>
                          {campaign.type === 'email' ? <Email fontSize="small" sx={{ mr: 0.5 }} /> : <Campaign fontSize="small" sx={{ mr: 0.5 }} />}
                          {campaign.type}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={campaign.status} 
                            color={statusColors[campaign.status]} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{campaign.audience}</TableCell>
                        <TableCell>{campaign.sentDate || 'Not sent'}</TableCell>
                        <TableCell>{campaign.openRate ? `${campaign.openRate}%` : '-'}</TableCell>
                        <TableCell>{campaign.clickRate ? `${campaign.clickRate}%` : '-'}</TableCell>
                        <TableCell>{campaign.conversionRate ? `${campaign.conversionRate}%` : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Campaigns Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                // Handle new campaign
              }}
            >
              New Campaign
            </Button>
          </Box>
          
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>All Campaigns</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Audience</TableCell>
                    <TableCell>Sent Date</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaignData.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>
                        {campaign.type === 'email' ? <Email fontSize="small" sx={{ mr: 0.5 }} /> : <Campaign fontSize="small" sx={{ mr: 0.5 }} />}
                        {campaign.type}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={campaign.status} 
                          color={statusColors[campaign.status]} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{campaign.audience}</TableCell>
                      <TableCell>{campaign.sentDate || 'Not sent'}</TableCell>
                      <TableCell>
                        {campaign.openRate ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {campaign.openRate}% open
                            </Typography>
                            <ArrowUpward fontSize="small" color="success" />
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" sx={{ mr: 1 }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ mr: 1 }}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Email Marketing Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Email Campaigns</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Sent Date</TableCell>
                      <TableCell>Open Rate</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaignData.filter(c => c.type === 'email').map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>{campaign.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={campaign.status} 
                            color={statusColors[campaign.status]} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{campaign.audience}</TableCell>
                        <TableCell>{campaign.sentDate || 'Not sent'}</TableCell>
                        <TableCell>{campaign.openRate ? `${campaign.openRate}%` : '-'}</TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            variant="outlined"
                            startIcon={campaign.status === 'draft' ? <Send /> : <Edit />}
                          >
                            {campaign.status === 'draft' ? 'Send' : 'View'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                >
                  New Email Campaign
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Email Templates</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1">Welcome Email</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Introduction email for new users
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small">Edit</Button>
                    <Button size="small">Preview</Button>
                  </CardActions>
                </Card>
                
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1">Newsletter</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monthly newsletter template
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small">Edit</Button>
                    <Button size="small">Preview</Button>
                  </CardActions>
                </Card>
                
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1">Promotion</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Special offer announcement
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small">Edit</Button>
                    <Button size="small">Preview</Button>
                  </CardActions>
                </Card>
                
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  fullWidth
                >
                  New Template
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Social Media Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Social Media Campaigns</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign Name</TableCell>
                      <TableCell>Platforms</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Engagement</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Summer Promo</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Facebook fontSize="small" color="primary" />
                          <Twitter fontSize="small" color="primary" />
                          <Instagram fontSize="small" color="primary" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label="Active" color="success" size="small" />
                      </TableCell>
                      <TableCell>2023-05-10</TableCell>
                      <TableCell>1,245 interactions</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined">View</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Feature Launch</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Facebook fontSize="small" color="primary" />
                          <Twitter fontSize="small" color="primary" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label="Scheduled" color="primary" size="small" />
                      </TableCell>
                      <TableCell>2023-06-01</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined">Edit</Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                >
                  New Social Campaign
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Social Accounts</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Facebook />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Facebook" 
                    secondary="Connected: AITradingOfficial"
                  />
                  <Button size="small" variant="outlined">Manage</Button>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Twitter />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Twitter" 
                    secondary="Connected: @AITrading"
                  />
                  <Button size="small" variant="outlined">Manage</Button>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Instagram />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Instagram" 
                    secondary="Connected: @aitrading"
                  />
                  <Button size="small" variant="outlined">Manage</Button>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LinkedIn />
                  </ListItemIcon>
                  <ListItemText 
                    primary="LinkedIn" 
                    secondary="Not connected"
                  />
                  <Button size="small" variant="contained">Connect</Button>
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Audience Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Audience Segments</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Segment Name</TableCell>
                      <TableCell>Users Count</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audienceSegments.map((segment) => (
                      <TableRow key={segment.id}>
                        <TableCell>{segment.name}</TableCell>
                        <TableCell>{segment.count}</TableCell>
                        <TableCell>2023-05-15</TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" sx={{ mr: 1 }}>Edit</Button>
                          <Button size="small" variant="outlined" color="primary">Target</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                >
                  New Segment
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Audience Overview</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Total Users: 8,731
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Active in last 30 days: 5,482 (62.8%)
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={62.8} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  User Plan Distribution
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Basic</Typography>
                    <Typography variant="body2">4,329 (49.6%)</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={49.6} 
                    color="primary"
                    sx={{ height: 8, borderRadius: 2, mb: 1 }}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Pro</Typography>
                    <Typography variant="body2">3,254 (37.3%)</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={37.3} 
                    color="secondary"
                    sx={{ height: 8, borderRadius: 2, mb: 1 }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Enterprise</Typography>
                    <Typography variant="body2">1,148 (13.1%)</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={13.1} 
                    color="success"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Box>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<InsertLink />}
              >
                Connect CRM
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default Marketing;
